import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableTitleWrapper,
  TableBoxesWrapper,
  SearchContainer,
} from 'pages/Account/pages/Profile/Forms/tableStyles';
import { PageSearchInput } from 'components/Layout/PageToolbar';
import { APIKeysNotice } from 'pages/Account/pages/Profile/Forms/APIKeys/styles';
import { InfiniteTable } from './InfiniteTable';
import { KizenTypography } from '__app/typography';
import type { IntegrationSecret } from '@kizen/api/integration-secrets';
import { MenuAction } from './types';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import { PageCard, SectionWrapper } from '../styles';
import type { AccessStruct } from '@kizen/permissions/types';
import {
  CreateIntegrationSecretModal,
  DeleteIntegrationSecretModal,
  EditIntegrationSecretModal,
  SelectedIntegrationSecret,
  ViewIntegrationSecretModal,
} from './IntegrationSecretModal';
import { StyledAddButton, StyledHeaderWrapper } from './styles';
import { useIsFetching } from '@tanstack/react-query';
import { integrationSecretsInfiniteQuery } from 'query/integration-secrets';
import { useSecretDeepLink } from './useSecretDeepLink';

type IntegrationSecretsProps = {
  access: AccessStruct;
};

export const IntegrationSecrets: FC<IntegrationSecretsProps> = ({ access }) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState<string>('');

  const [selectedIntegrationSecret, setSelectedIntegrationSecret] =
    useState<SelectedIntegrationSecret>({
      id: '',
      data: null,
      action: null,
    });

  const onSelectAction = useCallback(
    (action: MenuAction, integrationSecret: IntegrationSecret | null) => {
      setSelectedIntegrationSecret({
        id: integrationSecret?.id || '',
        data: integrationSecret,
        action,
      });
    },
    []
  );

  const onAddIntegrationSecret = () => {
    onSelectAction('create', null);
  };

  useSecretDeepLink(onSelectAction, access);

  const isFetching = useIsFetching({
    exact: false,
    queryKey: integrationSecretsInfiniteQuery().queryKey,
  });

  return (
    <>
      <TableTitleWrapper>
        <KizenTypography as="h3" type="subheader">
          {t('Integration Secrets')}
        </KizenTypography>
        <StyledHeaderWrapper>
          {access.edit ? (
            <StyledAddButton variant="text" onClick={onAddIntegrationSecret}>
              {t('+ Add Integration Secret')}
            </StyledAddButton>
          ) : null}
          <SearchContainer>
            <PageSearchInput
              placeholder={t('Find Secrets')}
              value={search}
              onChange={setSearch}
              loading={!!isFetching}
            />
          </SearchContainer>
        </StyledHeaderWrapper>
      </TableTitleWrapper>
      <TableBoxesWrapper>
        <APIKeysNotice>
          <KizenTypography as="div">
            {t(
              '(For Techies) Integration Secrets allow businesses to enable access to external services while keeping the secrets secure and enabling rotations, as needed.'
            )}
          </KizenTypography>
        </APIKeysNotice>
      </TableBoxesWrapper>
      <InfiniteTable
        onSelectAction={onSelectAction}
        search={search}
        access={access}
      />
      {selectedIntegrationSecret.action === 'create' ? (
        <CreateIntegrationSecretModal
          setSelectedIntegrationSecret={setSelectedIntegrationSecret}
        />
      ) : null}
      {selectedIntegrationSecret.action === 'edit' ? (
        <EditIntegrationSecretModal
          selectedIntegrationSecret={selectedIntegrationSecret}
          setSelectedIntegrationSecret={setSelectedIntegrationSecret}
        />
      ) : null}
      {selectedIntegrationSecret.action === 'view' ? (
        <ViewIntegrationSecretModal
          selectedIntegrationSecret={selectedIntegrationSecret}
          setSelectedIntegrationSecret={setSelectedIntegrationSecret}
        />
      ) : null}
      {selectedIntegrationSecret.action === 'delete' ? (
        <DeleteIntegrationSecretModal
          selectedIntegrationSecret={selectedIntegrationSecret}
          setSelectedIntegrationSecret={setSelectedIntegrationSecret}
        />
      ) : null}
    </>
  );
};

export const IntegrationSecretsPage: FC<{
  access: { manageIntegrationSecrets: AccessStruct };
}> = ({ access }) => (
  <SectionWrapper>
    <ContentWidth>
      <PageCard data-qa="manage-connections__integration-secrets">
        <IntegrationSecrets access={access.manageIntegrationSecrets} />
      </PageCard>
    </ContentWidth>
  </SectionWrapper>
);
